import React from 'react';
import { AiOutlineDollar } from 'react-icons/ai';
import { C, CARD, H2, SUB, BTN_PRIMARY, BTN_SECONDARY, btn, Notice, Field, TextInput, TaskRow, BUSY } from './ui';
import { VERIFY_DELAYS_MS, companyName, round2 } from './wizardLogic';

const LEVELS = [
    { id: 'level1Price', label: 'Level 1', required: true },
    { id: 'level2Price', label: 'Level 2' },
    { id: 'level3Price', label: 'Level 3' },
    { id: 'level4Price', label: 'Level 4' },
];

const marginOf = (price, cost) => {
    const p = Number(price);
    const c = Number(cost);
    if (!p || p <= 0 || !isFinite(c) || String(cost).trim() === '') return null;
    return round2(((p - c) / p) * 100);
};

// Precios opcionales: costo → Sage; Level 1-4 → portal (Price Management).
export default function PricesStep({ draft, idCompany, role, floor, isAdmin, locked, busy, errors, onField, onSave, onSkip, onUndoSkip, onRetry }) {
    const c = draft.companies[idCompany];
    const p = c.prices;
    const f = p.form;
    const anyBusy = busy || BUSY.indexOf(p.costTask.status) >= 0 || p.portalTask.status === 'saving';
    const started = p.costTask.status !== 'idle' || p.portalTask.status !== 'idle';
    const readOnly = locked || anyBusy || p.skipped || p.costTask.sent;
    const levelsReadOnly = locked || anyBusy || p.skipped || p.portalTask.status === 'done';
    const money = (v) => v.replace(/[^\d.]/g, '');

    return (
        <div style={CARD}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <AiOutlineDollar style={{ fontSize: '1.4rem', color: C.primary, flexShrink: 0 }} aria-hidden="true" />
                <div>
                    <h2 style={H2}>Prices &amp; Cost · {companyName(idCompany)}</h2>
                    <p style={{ ...SUB, marginTop: 0 }}>{role} company · {draft.product.form.itemCode} — optional</p>
                </div>
            </div>

            <Notice kind="info">The cost is sent to Sage. Level 1–4 prices are saved in the portal, same as Price Management.</Notice>
            {!isAdmin && <Notice kind="warn">Only administrators can save portal prices. You can still skip this step and have an admin set them in Price Management.</Notice>}

            {p.skipped ? (
                <>
                    <Notice kind="warn">
                        Skipped. <b>{draft.product.form.itemCode}</b> stays <b>inactive in {companyName(idCompany)}</b> (not shown in the catalog) until it has a cost and a sale price.
                    </Notice>
                    {!locked && <button type="button" style={{ ...BTN_SECONDARY, marginTop: '0.75rem' }} onClick={onUndoSkip} disabled={busy}>Enter prices instead</button>}
                </>
            ) : (
                <>
                    <div className="row g-3" style={{ marginTop: '0.5rem' }}>
                        <div className="col-12 col-md-4">
                            <Field id="pr-cost" label="Unit cost (Sage)" required error={errors.cost}>
                                <TextInput id="pr-cost" inputMode="decimal" placeholder="0.00" value={f.cost} error={errors.cost} readOnly={readOnly} onChange={(v) => onField('cost', money(v))} />
                            </Field>
                        </div>
                    </div>
                    <div className="row g-3" style={{ marginTop: '0.25rem' }}>
                        {LEVELS.map((l) => {
                            const m = marginOf(f[l.id], f.cost);
                            return (
                                <div className="col-6 col-md-3" key={l.id}>
                                    <Field id={`pr-${l.id}`} label={l.label} required={!!l.required} error={errors[l.id]}>
                                        <TextInput id={`pr-${l.id}`} inputMode="decimal" placeholder="0.00" value={f[l.id]} error={errors[l.id]} readOnly={levelsReadOnly} onChange={(v) => onField(l.id, money(v))} />
                                    </Field>
                                    {m !== null && !errors[l.id] && (
                                        <span style={{ display: 'inline-block', marginTop: '4px', fontSize: '0.72rem', fontWeight: 700, borderRadius: '6px', padding: '2px 6px', color: m < 10 ? C.err : C.ok, background: m < 10 ? C.errBg : C.okBg }}>{m}% margin</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <p style={{ ...SUB, fontSize: '0.78rem' }}>
                        {floor !== null ? `Minimum allowed price: $${floor.toFixed(2)}.` : 'The minimum price is checked by the portal when saving.'}
                    </p>

                    {!started && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
                            <button type="button" style={{ ...btn(BTN_PRIMARY, anyBusy || !isAdmin), width: '100%' }} disabled={anyBusy || !isAdmin} onClick={onSave}>Save cost &amp; prices</button>
                            <button type="button" style={{ ...BTN_SECONDARY, width: '100%' }} disabled={anyBusy} onClick={onSkip}>Skip for now</button>
                        </div>
                    )}

                    {started && (
                        <div style={{ marginTop: '1rem' }}>
                            <TaskRow title="Cost in Sage" detail={f.cost ? `$${Number(f.cost).toFixed(2)}` : ''} task={p.costTask} total={VERIFY_DELAYS_MS.length} onRetry={anyBusy ? null : onRetry} />
                            <TaskRow title="Prices in the portal" detail="Level 1–4" task={p.portalTask} onRetry={anyBusy || p.costTask.status !== 'done' ? null : onRetry} />
                            {p.portalTask.status !== 'done' && !anyBusy && (
                                <button type="button" style={{ ...BTN_SECONDARY, width: '100%', marginTop: '0.5rem' }} onClick={onSkip}>Skip for now</button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
